import React, { useRef, useState, useEffect, ReactNode, HTMLAttributes } from "react";
import Box, { BoxProps } from "@mui/material/Box"; // Using Box for styling and sx prop
import ToolTipComponent from "../tooltip";

interface TextProps extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
  children?: ReactNode;
  className?: string;
  tooltipContent?: ReactNode;
  alwaysShowTooltip?: boolean;
  sx?: BoxProps["sx"];
  placement?: any;
}

const Text: React.FC<TextProps> = ({
  children,
  className,
  tooltipContent,
  alwaysShowTooltip = false,
  style,
  sx,
  placement,
  ...rest
}) => {
  const textRef = useRef<HTMLDivElement>(null);
  const [isOverflowed, setIsOverflowed] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    const checkOverflow = () => {
      if (el) {
        const hasOverflow = el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight;
        if (hasOverflow !== isOverflowed) {
          setIsOverflowed(hasOverflow);
        }
      }
    };

    checkOverflow();

    let resizeObserver: ResizeObserver;
    if (el) {
      resizeObserver = new ResizeObserver(checkOverflow);
      resizeObserver.observe(el);
    }

    return () => {
      if (el && resizeObserver) {
        resizeObserver.unobserve(el);
      }
    };
  }, [children, isOverflowed]);

  let tooltipTitle: any = "";
  let showTooltip = false;

  if (alwaysShowTooltip || isOverflowed) {
    showTooltip = true;
    tooltipTitle = tooltipContent || children || "";
  }

  return (
    <ToolTipComponent
      content={showTooltip ? tooltipTitle : ""}
      disableHoverListener={!showTooltip}
      arrow
      position={placement ?? "top"}
    >
      <Box
        ref={textRef}
        className={className}
        style={style}
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          width: "100%",
          ...sx,
        }}
        {...rest}
      >
        {children}
      </Box>
    </ToolTipComponent>
  );
};

export default Text;
